import { memo } from "react";
import { Handle, Position, NodeProps } from "@xyflow/react";
import {
  FlowEditorAgentNode,
  useAgentWorkflowStore,
} from "@/store/agentsStore";
import { useAgentWorkflowActionsStore } from "@/store/agentWorkflowActionsStore";
import { NodeWrapper } from "./NodeWrapper";
import { NodeErrorDisplay } from "./NodeErrorDisplay";
import { Bot, Sparkles } from "lucide-react";

export const AgentNode = memo(
  ({ data, selected }: NodeProps<FlowEditorAgentNode>) => {
    const nodeData = data.node;
    const workflowVersionId = data.workflowVersionId;

    const getActionById = useAgentWorkflowActionsStore(
      (state) => state.getActionById,
    );
    const action = nodeData.actionId
      ? getActionById(nodeData.actionId)
      : undefined;

    // Get skill actions for display
    const skillActions =
      nodeData.actions
        ?.slice(0, 5)
        .map((skill) => getActionById(skill.actionId))
        .filter(Boolean) || [];

    // Look up errors from the workflow version store
    const workflowVersion = useAgentWorkflowStore((state) =>
      workflowVersionId
        ? state.getWorkflowVersionById(workflowVersionId)
        : undefined,
    );
    const nodeErrors = workflowVersion?.errors?.[nodeData.id];

    return (
      <NodeWrapper
        selected={selected}
        nodeType="agent"
        className="px-3 py-2.5 min-w-[200px] bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200"
      >
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-blue-400 !border-2 !border-white"
        />

        <NodeErrorDisplay errors={nodeErrors || []} />

        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-500 rounded">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-900 flex-1">
            {action?.name}
          </span>
        </div>

        {/* Display skill icons */}
        {skillActions.length > 0 && (
          <div className="flex items-center gap-1 mt-2">
            {skillActions.map((skillAction, index) => (
              <div
                key={index}
                className="w-6 h-6 rounded bg-white border border-gray-200 flex items-center justify-center"
                title={skillAction?.name}
              >
                {skillAction?.imageUrl ? (
                  <img
                    src={skillAction.imageUrl}
                    alt={skillAction.name}
                    className="w-4 h-4 object-contain"
                  />
                ) : (
                  <Bot className="w-3 h-3 text-gray-400" />
                )}
              </div>
            ))}
            {nodeData.actions && nodeData.actions.length > 5 && (
              <div className="text-xs text-gray-500 ml-1">
                +{nodeData.actions.length - 5}
              </div>
            )}
          </div>
        )}

        <Handle
          type="source"
          position={Position.Bottom}
          // Like ConditionNode, AgentNode allows multiple outgoing edges
          isConnectableStart={true}
          className="w-3 h-3 !bg-blue-400 !border-2 !border-white"
        />
      </NodeWrapper>
    );
  },
);
